'use client';

/**
 * Auto-load-more sentinel.
 *
 * Drop this in place of a "Load more" button at the bottom of any paginated
 * list. When the sentinel scrolls within `rootMargin` of the viewport, it
 * fires `onLoadMore()` once. While the fetch is in flight, it renders a
 * three-dot pulse animation. When `hasMore` becomes false, it shows an
 * end-of-feed marker.
 *
 * Why an IntersectionObserver instead of a scroll listener? Scroll handlers
 * fire 60 times per second and force layout reads; the observer is fired by
 * the browser only when the threshold is actually crossed, costs ~zero CPU
 * the rest of the time, and works through any scroll container (we use the
 * viewport here).
 *
 * Reentrancy guard: `loading` from the parent OR an in-flight fetch tracked
 * locally prevents back-to-back fires while the user is still near the
 * bottom. The observer keeps the sentinel "intersecting" until the new
 * batch lands and the parent re-renders, pushing the sentinel back below
 * the fold.
 */
import { useEffect, useRef } from 'react';

interface Props {
  /** Callback to fetch the next page. */
  onLoadMore: () => void;
  /** True while the parent is fetching — disables the observer to avoid
   *  duplicate fires for the same page. */
  loading: boolean;
  /** False = sentinel becomes a "you've seen everything" marker. */
  hasMore: boolean;
  /** Pixels of pre-load distance. 400px means we start fetching when the
   *  sentinel is 400px below the viewport bottom — feels seamless to the
   *  user. */
  rootMargin?: string;
  /** Optional label override for the "end of feed" state. */
  endLabel?: string;
}

export default function InfiniteScrollSentinel({
  onLoadMore,
  loading,
  hasMore,
  rootMargin = '0px 0px 400px 0px',
  endLabel = "You've reached the end.",
}: Props) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Track the last fired callback identity so the observer effect can react
  // to it changing without losing its registered observer instance.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!hasMore || loading) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onLoadMoreRef.current();
            // Disconnect immediately so a flicker of the sentinel staying in
            // view doesn't fire twice. The observer is recreated by the
            // effect when `loading` flips back to false.
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, rootMargin]);

  if (!hasMore) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          padding: '40px 0 24px',
          color: 'var(--ink-3)',
          fontSize: 13,
        }}
      >
        <span
          aria-hidden
          style={{
            display: 'block',
            width: 28, height: 1,
            background: 'var(--line)',
            margin: '0 auto 6px',
          }}
        />
        <span style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
          {endLabel}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={sentinelRef}
      role="status"
      aria-live="polite"
      aria-label={loading ? 'Loading more prompts' : 'Scroll to load more'}
      style={{
        display: 'grid', placeItems: 'center',
        padding: '40px 0 32px',
      }}
    >
      <ThreeDotPulse />
    </div>
  );
}

/**
 * Subtle pulsing dots — three circles staggered with a `pulse` keyframe.
 * Looks at home with the editorial palette and doesn't fight the page's
 * serif/typographic feel the way a giant spinner would.
 */
function ThreeDotPulse() {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          aria-hidden
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'var(--accent)',
            animation: `ps-dot-pulse 1.2s ${i * 0.15}s infinite ease-in-out`,
            display: 'inline-block',
          }}
        />
      ))}
      <style jsx>{`
        @keyframes ps-dot-pulse {
          0%, 80%, 100% {
            opacity: 0.25;
            transform: scale(0.8);
          }
          40% {
            opacity: 1;
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
}
